import { useContext, useEffect, useRef, useState, type FC } from "react";
import { Button, Checkbox, Input, Modal, SelectPicker, Toggle } from "rsuite";
import { Role } from "../../../models/system/role";
import { LoadingContext } from "../../../contexts/LoadingContext";
import { getPermissions } from "../../../services/inventory/SystemApi";
import { errorToast } from "../../../utils/helper-ui";

interface RoleFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: Role) => void;
  role?: Role;
}

const RoleForm: FC<RoleFormProps> = ({ open, role, onClose, onSubmit }) => {
  const [data, setData] = useState<Role>({
    id: "",
    name: "",
    is_admin: true,
    is_super_admin: false,
    is_merchant: false,
    is_owner: false,
  });
  const [uploading, setUploading] = useState(false);
  const [token, setToken] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);
  const [roles, setRoles] = useState<Role[]>([]);
  const { isLoading, setIsLoading } = useContext(LoadingContext);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [mode, setMode] = useState("admin");
  const [permissionGrops, setPermissionGrops] =
    useState<Record<string, Record<string, string[]>>>();

  useEffect(() => {
    setData(
      role || {
        id: "",
        name: "",
        is_admin: true,
        is_super_admin: false,
        is_merchant: false,
        is_owner: false,
      }
    );
    if (role?.permissions) {
      setPermissions((role?.permissions ?? []).map((p) => p.name));
    }
    console.log("role", role);
    if (role) {
      setMode(role.is_admin ? "admin" : "merchant");
    }
  }, [role]);

  useEffect(() => {
    getPermissions()
      .then((v) => v.json())
      .then((response) => {
        let groups: Record<string, Record<string, string[]>> = {};
        for (const permission of response.data) {
          let splitPermission = permission.name.split(":");
          groups[splitPermission[0]] = groups[splitPermission[0]] || [];
          groups[splitPermission[0]][splitPermission[1]] =
            groups[splitPermission[0]][splitPermission[1]] || [];
          groups[splitPermission[0]][splitPermission[1]].push(
            splitPermission[2]
          );
        }
        setPermissionGrops(groups);
      })
      .catch((error) => {
        errorToast(error);
      });
  }, []);

  useEffect(() => {
    // console.log("permissions", permissions);
  }, [permissions]);
  return (
    <Modal
      size={"lg"}
      open={open}
      onClose={() => {
        onClose();
        setData({
          id: "",
          name: "",
          is_admin: true,
          is_super_admin: false,
          is_merchant: false,
          is_owner: false,
        });
        setPermissions([]);
      }}
    >
      <Modal.Header>
        <Modal.Title>Role Form</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="w-full">
          <div className="form-group">
            <label>Name</label>
            <Input
              type="text"
              placeholder="Input role name"
              value={data.name}
              onChange={(e) => setData({ ...data, name: e! })}
            />
          </div>
          <div className="form-group">
            <label>Mode</label>
            <div className="input">
              <SelectPicker
                block
                data={[
                  { label: "Admin", value: "admin" },
                  { label: "Merchant", value: "merchant" },
                ]}
                value={mode}
                onChange={(value) => setMode(value!)}
              />
            </div>
          </div>
          {mode === "admin" ? (
            <div className="form-group">
              <label>Super Admin</label>
              <div className="input">
                <Toggle
                  checked={data.is_super_admin}
                  onChange={(checked) =>
                    setData({ ...data, is_super_admin: checked })
                  }
                />
              </div>
            </div>
          ) : (
            <div className="form-group">
              <label>Owner</label>
              <div className="input">
                <Toggle
                  checked={data.is_owner}
                  onChange={(checked) =>
                    setData({ ...data, is_owner: checked })
                  }
                />
              </div>
            </div>
          )}
          {!data.is_super_admin && (
            <div className="form-group">
              <label>Permissions</label>
              <div className="input">
                <div className="grid grid-cols-3 gap-2">
                  {Object.keys(permissionGrops || {}).map((group, index) => (
                    <div className="border p-2 rounded-lg" key={index}>
                      <h3 className="uppercase font-bold text-xl text-blue-600">
                        {group}
                      </h3>
                      {Object.keys(permissionGrops?.[group] || {}).map(
                        (permission, i) => (
                          <div className="mb-2" key={i}>
                            <div className="flex items-center justify-between">
                              <h4 className="uppercase font-bold text-sm">
                                {permission.split("_").join(" ")}
                              </h4>
                              <Checkbox
                                indeterminate={
                                  permissionGrops?.[group]?.[permission].some(
                                    (item) =>
                                      permissions.includes(
                                        `${group}:${permission}:${item}`
                                      )
                                  ) &&
                                  !permissionGrops?.[group]?.[permission].every(
                                    (item) =>
                                      permissions.includes(
                                        `${group}:${permission}:${item}`
                                      )
                                  )
                                }
                                checked={permissionGrops?.[group]?.[
                                  permission
                                ].every((item) =>
                                  permissions.includes(
                                    `${group}:${permission}:${item}`
                                  )
                                )}
                                onChange={(value, checked) => {
                                  if (checked) {
                                    let check =
                                      permissionGrops?.[group]?.[
                                        permission
                                      ].map(
                                        (item) =>
                                          `${group}:${permission}:${item}`
                                      ) ?? [];
                                    setPermissions([...permissions, ...check]);
                                  } else {
                                    let uncheck =
                                      permissionGrops?.[group]?.[
                                        permission
                                      ].map(
                                        (item) =>
                                          `${group}:${permission}:${item}`
                                      ) ?? [];
                                    setPermissions(
                                      permissions.filter(
                                        (v) => !uncheck.includes(v)
                                      )
                                    );
                                  }
                                }}
                              />
                            </div>
                            {permissionGrops?.[group]?.[permission].map(
                              (item, indexItem) => (
                                <div key={indexItem}>
                                  <Checkbox
                                    checked={permissions.includes(
                                      `${group}:${permission}:${item}`
                                    )}
                                    onChange={(value, checked) => {
                                      if (checked) {
                                        setPermissions([
                                          ...permissions,
                                          `${group}:${permission}:${item}`,
                                        ]);
                                      } else {
                                        setPermissions(
                                          permissions.filter(
                                            (v) =>
                                              v !==
                                              `${group}:${permission}:${item}`
                                          )
                                        );
                                      }
                                    }}
                                  />
                                  <span>{item}</span>
                                </div>
                              )
                            )}
                          </div>
                        )
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="pt-4">
          <Button
            appearance="primary"
            loading={isLoading}
            onClick={() => {
              if (mode === "admin") {
                data.is_admin = true;
                data.is_merchant = false;
              }
              if (mode === "merchant") {
                data.is_merchant = true;
                data.is_admin = false;
              }
              if (data.is_super_admin) {
                data.permission_names = [];
              } else {
                data.permission_names = permissions;
              }

              onSubmit(data);
              onClose();
            }}
          >
            Submit
          </Button>
          <Button appearance="link" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
export default RoleForm;
